/**
 * Catálogo dos 22 produtos do setor NSA do GOES-19 publicados pela NOAA/STAR —
 * CONTRATO COMPARTILHADO (dono: app-core; outros módulos só leem).
 *
 * `id` é o nome do diretório no CDN do STAR. Os textos são para o cidadão:
 * curtos, sem jargão, dizendo o que se vê e para que serve.
 */

export type ProductCategory = 'composicao' | 'visivel' | 'infravermelho-proximo' | 'infravermelho';

export interface GoesProduct {
  /** Diretório no CDN do STAR (ex.: "GEOCOLOR", "13"). */
  id: string;
  /** Nome curto exibido na interface. */
  name: string;
  /** Rótulo técnico (banda e comprimento de onda, ou nome da composição). */
  technical: string;
  category: ProductCategory;
  /** O que a imagem mostra, em linguagem simples. */
  description: string;
  /** Só tem informação útil durante o dia (bandas refletidas). */
  dayOnly: boolean;
}

export const CATEGORY_LABELS: Record<ProductCategory, string> = {
  composicao: 'Composições coloridas',
  visivel: 'Luz visível',
  'infravermelho-proximo': 'Infravermelho próximo',
  infravermelho: 'Infravermelho (dia e noite)',
};

export const PRODUCTS: GoesProduct[] = [
  // Composições coloridas (RGB)
  {
    id: 'GEOCOLOR',
    name: 'Cor natural (GeoColor)',
    technical: 'GeoColor — CIRA/NOAA',
    category: 'composicao',
    description:
      'Parece uma foto: de dia mostra a Terra em cores naturais; à noite, nuvens em tons de azul e as luzes das cidades. Boa para ver fumaça durante o dia.',
    dayOnly: false,
  },
  {
    id: 'FireTemperature',
    name: 'Temperatura do fogo',
    technical: 'Fire Temperature RGB',
    category: 'composicao',
    description:
      'Destaca áreas quentes: incêndios aparecem em vermelho, laranja e amarelo (quanto mais claro, mais quente). Funciona melhor de dia.',
    dayOnly: true,
  },
  {
    id: 'Sandwich',
    name: 'Tempestades (Sandwich)',
    technical: 'Sandwich RGB — bandas 3 e 13',
    category: 'composicao',
    description:
      'Combina a textura das nuvens com a temperatura do topo delas. Tempestades fortes aparecem em cores vivas. Só de dia.',
    dayOnly: true,
  },
  {
    id: 'AirMass',
    name: 'Massas de ar',
    technical: 'Air Mass RGB',
    category: 'composicao',
    description:
      'Mostra massas de ar quente/úmido e frio/seco em cores diferentes. Útil para acompanhar frentes e o tempo em grande escala.',
    dayOnly: false,
  },
  {
    id: 'Dust',
    name: 'Poeira',
    technical: 'Dust RGB',
    category: 'composicao',
    description: 'Realça nuvens de poeira (em rosa/magenta), como a poeira do Saara que chega à Amazônia.',
    dayOnly: false,
  },
  {
    id: 'DayNightCloudMicroCombo',
    name: 'Nuvens dia e noite',
    technical: 'Day Night Cloud Micro Combo RGB',
    category: 'composicao',
    description: 'Ajuda a distinguir tipos de nuvem e nevoeiro, de dia e de noite, com a mesma lógica de cores.',
    dayOnly: false,
  },

  // Visível
  {
    id: '01',
    name: 'Azul',
    technical: 'Banda 1 — 0,47 µm',
    category: 'visivel',
    description: 'Luz azul refletida. Boa para ver fumaça, poeira e poluição no ar. Só de dia.',
    dayOnly: true,
  },
  {
    id: '02',
    name: 'Vermelho (alta resolução)',
    technical: 'Banda 2 — 0,64 µm',
    category: 'visivel',
    description: 'A imagem mais detalhada do satélite, em preto e branco: nuvens, fumaça e rios. Só de dia.',
    dayOnly: true,
  },

  // Infravermelho próximo
  {
    id: '03',
    name: 'Vegetação',
    technical: 'Banda 3 — 0,86 µm',
    category: 'infravermelho-proximo',
    description: 'A vegetação saudável aparece clara. Mostra áreas queimadas (mais escuras) e o contraste terra/água. Só de dia.',
    dayOnly: true,
  },
  {
    id: '04',
    name: 'Cirros',
    technical: 'Banda 4 — 1,37 µm',
    category: 'infravermelho-proximo',
    description: 'Mostra nuvens finas e altas (cirros), difíceis de ver nas outras imagens. Só de dia.',
    dayOnly: true,
  },
  {
    id: '05',
    name: 'Neve e gelo',
    technical: 'Banda 5 — 1,6 µm',
    category: 'infravermelho-proximo',
    description: 'Separa nuvens de gelo de nuvens de água. Também realça áreas queimadas recentes. Só de dia.',
    dayOnly: true,
  },
  {
    id: '06',
    name: 'Partículas de nuvem',
    technical: 'Banda 6 — 2,2 µm',
    category: 'infravermelho-proximo',
    description: 'Indica o tamanho das gotas e cristais nas nuvens e ajuda a ver focos de calor muito intensos. Só de dia.',
    dayOnly: true,
  },

  // Infravermelho (termal)
  {
    id: '07',
    name: 'Ondas curtas (focos de calor)',
    technical: 'Banda 7 — 3,9 µm',
    category: 'infravermelho',
    description: 'Muito sensível a calor: focos de incêndio aparecem como pontos escuros/quentes. Também mostra nevoeiro à noite.',
    dayOnly: false,
  },
  {
    id: '08',
    name: 'Vapor d’água (alto)',
    technical: 'Banda 8 — 6,2 µm',
    category: 'infravermelho',
    description: 'Umidade nas camadas altas da atmosfera. Áreas escuras são ar seco.',
    dayOnly: false,
  },
  {
    id: '09',
    name: 'Vapor d’água (médio)',
    technical: 'Banda 9 — 6,9 µm',
    category: 'infravermelho',
    description: 'Umidade nas camadas médias da atmosfera.',
    dayOnly: false,
  },
  {
    id: '10',
    name: 'Vapor d’água (baixo)',
    technical: 'Banda 10 — 7,3 µm',
    category: 'infravermelho',
    description: 'Umidade nas camadas mais baixas que o satélite consegue ver; às vezes mostra plumas de fumaça e vulcões.',
    dayOnly: false,
  },
  {
    id: '11',
    name: 'Fase das nuvens',
    technical: 'Banda 11 — 8,4 µm',
    category: 'infravermelho',
    description: 'Ajuda a saber se o topo das nuvens é de gelo ou de água.',
    dayOnly: false,
  },
  {
    id: '12',
    name: 'Ozônio',
    technical: 'Banda 12 — 9,6 µm',
    category: 'infravermelho',
    description: 'Relacionada ao ozônio na alta atmosfera; usada em estudos do tempo em grande escala.',
    dayOnly: false,
  },
  {
    id: '13',
    name: 'Infravermelho “limpo”',
    technical: 'Banda 13 — 10,3 µm',
    category: 'infravermelho',
    description:
      'Temperatura do topo das nuvens, de dia e de noite. Cores vivas indicam nuvens muito altas e frias, típicas de tempestades.',
    dayOnly: false,
  },
  {
    id: '14',
    name: 'Infravermelho',
    technical: 'Banda 14 — 11,2 µm',
    category: 'infravermelho',
    description: 'Infravermelho clássico: temperatura das nuvens e da superfície, dia e noite.',
    dayOnly: false,
  },
  {
    id: '15',
    name: 'Infravermelho “sujo”',
    technical: 'Banda 15 — 12,3 µm',
    category: 'infravermelho',
    description: 'Parecida com a banda 13, mas mais sensível à umidade. Ajuda a detectar poeira e cinzas.',
    dayOnly: false,
  },
  {
    id: '16',
    name: 'Dióxido de carbono',
    technical: 'Banda 16 — 13,3 µm',
    category: 'infravermelho',
    description: 'Ajuda a estimar a altura das nuvens.',
    dayOnly: false,
  },
];

const BY_ID = new Map(PRODUCTS.map((p) => [p.id, p]));

export function getProduct(id: string): GoesProduct | undefined {
  return BY_ID.get(id);
}

export function getProductName(id: string): string {
  return BY_ID.get(id)?.name ?? id;
}

/** Produto padrão ao abrir o portal. */
export const DEFAULT_PRODUCT_ID = 'GEOCOLOR';

/** Máximo de camadas GOES empilhadas. */
export const MAX_GOES_LAYERS = 3;
